/**
 * Galaxy / iPhone 전사 파서 (TRANSCRIPT_PROTOCOL §4).
 * - 하나의 엄격한 정규식에 의존하지 않는다: 머리줄 변형(공백, 시:분:초)을 허용한다.
 * - 원문은 바꾸지 않는다. 각 턴/블록은 원문 줄 번호를 가진다 (RAW 역추적, §15).
 * - 해석하지 못한 줄은 버리지 않고 직전 턴의 본문으로 붙이며, 경고 개수만 센다 (내용은 경고에 넣지 않음).
 */
import { parseTimestampMs } from './detect';

const TS = String.raw`(?:\d{1,2}:)?\d{1,2}:\d{2}`;
const GALAXY_HEADER = new RegExp(String.raw`^\s*발화자\s*(\d+)\s*\(\s*(${TS})\s*\)\s*$`);
const IPHONE_HEADER = new RegExp(String.raw`^\s*(${TS})\s+참석자\s*(\d+)\s*$`);
const SECTION = /^\s*(galaxy|갤럭시|삼성|samsung|iphone|아이폰|apple|애플)\s*:?\s*$/i;
const RULE = /^\s*_{3,}\s*$/;

export type RawTurn = {
  index: number;
  /** 기기 원 라벨 ("발화자 2", "참석자 1") — 임상 역할이 아니다 */
  label: string;
  startMs: number;
  text: string;
  lineStart: number; // 1-based, 머리줄
  lineEnd: number;
};

export type ParseResult = { turns: RawTurn[]; warnings: string[] };

function parseWith(
  text: string,
  header: RegExp,
  pick: (m: RegExpExecArray) => { label: string; ts: string },
): ParseResult {
  const lines = text.split(/\r?\n/);
  const turns: RawTurn[] = [];
  let orphanLines = 0;
  let cur: RawTurn | null = null;
  const body: string[] = [];
  const close = (endLine: number) => {
    if (!cur) return;
    cur.text = body.join(' ').replace(/\s+/g, ' ').trim();
    cur.lineEnd = endLine;
    turns.push(cur);
    body.length = 0;
  };
  lines.forEach((line, i) => {
    const m = header.exec(line);
    if (m) {
      close(i);
      const { label, ts } = pick(m);
      cur = { index: turns.length, label, startMs: parseTimestampMs(ts), text: '', lineStart: i + 1, lineEnd: i + 1 };
      return;
    }
    if (SECTION.test(line) || RULE.test(line)) return;
    if (!line.trim()) return;
    if (cur) body.push(line.trim());
    else orphanLines++;
  });
  close(lines.length);
  const warnings: string[] = [];
  if (orphanLines) warnings.push(`orphan-lines:${orphanLines}`);
  for (let i = 1; i < turns.length; i++) {
    if (turns[i]!.startMs < turns[i - 1]!.startMs) {
      warnings.push(`time-backwards:line-${turns[i]!.lineStart}`);
      break;
    }
  }
  return { turns, warnings };
}

export function parseGalaxy(text: string): ParseResult {
  return parseWith(text, GALAXY_HEADER, (m) => ({ label: `발화자 ${m[1]}`, ts: m[2]! }));
}

export function parseIphone(text: string): ParseResult {
  return parseWith(text, IPHONE_HEADER, (m) => ({ label: `참석자 ${m[2]}`, ts: m[1]! }));
}
